// i18n bootstrap for the CANDIDATE-facing app (react-i18next).
//
// Language model (candidate side):
//   1. If the candidate explicitly chose a language via the top-right switcher,
//      that per-device choice (localStorage) WINS and sticks.
//   2. Otherwise the page language is driven by the JOB's content_language —
//      Japanese jobs (content_language === "ja") render in Japanese, everything
//      else in English. This is applied by JobLanguageSync from the job fetch.
//   3. Job-less routes (privacy/terms/unsubscribe) default to English.
//
// Only English + Japanese are supported here (Japan jobs are the only localized
// case); Spanish is intentionally not offered on the candidate side.
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import ja from "./locales/ja.json";

export const SUPPORTED_LANGUAGES = [
    { code: "en", label: "English", short: "EN" },
    { code: "ja", label: "日本語", short: "日本語" },
];

export const LANG_STORAGE_KEY = "cp_candidate_lang";

// The candidate's explicit switcher choice (per device). "" when none.
export function getStoredOverride() {
    try {
        const v = window.localStorage.getItem(LANG_STORAGE_KEY);
        return SUPPORTED_LANGUAGES.some((l) => l.code === v) ? v : "";
    } catch (_e) {
        return "";
    }
}

i18n.use(initReactI18next).init({
    resources: {
        en: { translation: en },
        ja: { translation: ja },
    },
    lng: getStoredOverride() || "en",
    fallbackLng: "en",
    supportedLngs: ["en", "ja"],
    interpolation: { escapeValue: false }, // React already escapes
    returnEmptyString: false, // fall back to en for empty/missing keys
});

// Switcher choice — persists per device and overrides the job language.
export function setLanguageOverride(code) {
    try {
        window.localStorage.setItem(LANG_STORAGE_KEY, code);
    } catch (_e) {
        /* best-effort */
    }
    i18n.changeLanguage(code);
}

// Job-driven language. Driven by the job's COUNTRY (Japan), not the JD's
// auto-detected content_language — a Japan job may have an English description.
//   • Japan job (country === "Japan"): default Japanese, but honor the
//     candidate's saved switcher choice (en/ja) if they picked one.
//   • Any other job (or no job): ALWAYS English, ignoring any stored override —
//     a non-Japan job has no switcher and must not inherit a ja preference.
export function applyJobLanguage(isJapanJob) {
    let lang = "en";
    if (isJapanJob) {
        const override = getStoredOverride();
        lang = override === "en" || override === "ja" ? override : "ja";
    }
    if (i18n.language !== lang) i18n.changeLanguage(lang);
}

export default i18n;
